import * as React from "react";
import {
  Table,
  TableCell,
  TableBody,
  TableHead,
  TableRow,
  Button,
  TextField,
  Select,
  MenuItem,
  OutlinedInput,
  InputLabel,
  FormControl,
  Tooltip,
} from "@mui/material";
import FileCopy from "@mui/icons-material/FileCopy";
import EditIcon from "@mui/icons-material/Edit";
import HowToReg from "@mui/icons-material/HowToReg";
import { DatePicker } from "@mui/x-date-pickers";
import RestUtilities, { IRestResponse } from "../../services/RestUtilities";
import EventStatus, { EventStatuses } from "../../models/EventStatus";
import EventModel from "../../models/Events";
import { RoutePaths } from "../../components/Routes";
import Loader from "../../components/Loader";
import Constants from "../../models/Constants";
import { Temporal } from "@js-temporal/polyfill";
import { GrowMessage } from "../../components/GrowMessage";
import { PropsWithRouter, withRouter } from '../../components/Shared/withNavigate';
import { useNavigate } from "react-router-dom";


class FilterModel {
  [index: string]: any;
  eventDescription: string = "";
  eventName: string = "";
  eventFrom: Date = null;
  eventTo: Date = null;
  activ: boolean;
  eventStatusId: number = EventStatuses.Offen;
}

interface EventsComponentProps extends PropsWithRouter {
}


export class EventsComponent extends React.Component<EventsComponentProps> {

  handleNavigate = (path:string) => {
    this.props.router.navigate(path);
  };

  public static TableColumns: string[] = [
    "ID",
    "Bezeichnung",
    "Beschreibung",
    "Datum",
    "Status",
    "Aktionen",
  ];

  state = {
    filter: new FilterModel(),
    data: [] as EventModel[],
    statuses: [] as EventStatus[],
    showError: false,
    errorMessage: "",
    loading: false,
  };

  handleFilterChange = (event: any): void => {
    const newFilter: FilterModel = { ...this.state.filter };
    const changedFilter: string = event.target.name;
    newFilter[changedFilter] = event.target.value;

    this.setState({ filter: newFilter });
  };

  handleSearch = () => {
    this.fetchEvents(this.state.filter);
  };

  clearFilters = (): void => {
    const emptyFilters = new FilterModel();
    this.setState({ filter: emptyFilters });
    this.fetchEvents();
  };

  formatDate = (date: Date): string => {
    const plainDate = Temporal.Instant.from(new Date(date).toISOString())
      .toZonedDateTimeISO("UTC")
      .toPlainDate();
    return `${plainDate.day.toString().padStart(2, "0")}.${plainDate.month
      .toString()
      .padStart(2, "0")}.${plainDate.year}`;
  };

  mapRowCells = (event: EventModel) => {
    return (
      <TableRow key={event.id}>
        <TableCell key={`${event.id}_${event.id}`}>{event.id}</TableCell>
        <TableCell key={`${event.id}_${event.eventName}`}>
          {event.eventName}
        </TableCell>
        <TableCell key={`${event.eventDescription}_${event.eventDescription}`}>
          {event.eventDescription}
        </TableCell>
        <TableCell key={`${event.eventFrom}`}>
          {`${this.formatDate(event.eventFrom)} - ${this.formatDate(
            event.eventTo
          )}`}
        </TableCell>
        <TableCell key={`${event.id}_${event.eventStatusId}`}>
          {EventStatuses[event.eventStatusId]}
        </TableCell>
        <TableCell key={`${event.id}_actions`}>
          <Tooltip title="Bearbeiten">
            <EditIcon
              className="icon"
              onClick={(e: any) =>
                this.handleNavigate(
                  `${RoutePaths.EditEvent}?eventid=${event.id}`
                )
              }
            />
          </Tooltip>
          <Tooltip title="Kopieren">
            <FileCopy
              className="icon"
              onClick={(e: any) => this.copyEvent(event.id)}
            />
          </Tooltip>
          <Tooltip title="Anmelden">
            <HowToReg
              className="icon"
              onClick={(e: any) =>
                this.handleNavigate(
                  `${RoutePaths.AddBooking}?eventId=${event.id}`
                )
              }
            />
          </Tooltip>
        </TableCell>
      </TableRow>
    );
  };

  fetchEventStatuses = () => {
    RestUtilities.get<EventStatus[]>("/api/EventStatus")
      .then((resp) => {
        let statuses: EventStatus[] = resp.content as EventStatus[];
        this.setState({ statuses: statuses });
      })
      .catch((resp) => {
        this.showError(resp);
      });
  };

  fetchEvents = (filter: FilterModel = new FilterModel()) => {
    this.setState({ loading: true });

    let url = "/api/Events?";

    if (filter.eventDescription !== "")
      url += `description=${filter.eventDescription}&`;

    if (filter.eventName !== "") url += `name=${filter.eventName}&`;

    if (typeof filter.eventFrom !== "undefined" && filter.eventFrom) {
      let date = new Date(filter.eventFrom);
      let dateString = `${date.getFullYear()}-${
        date.getMonth() + 1
      }-${date.getDate()}`;
      url += `eventFrom=${dateString}&`;
    }

    if (typeof filter.eventTo !== "undefined" && filter.eventTo) {
      let date = new Date(filter.eventTo);
      let dateString = `${date.getFullYear()}-${
        date.getMonth() + 1
      }-${date.getDate()}`;
      url += `eventTo=${dateString}&`;
    }

    if (typeof filter.eventStatusId !== "undefined") {
      url += `eventStatusId=${filter.eventStatusId}&`;
    }

    if (typeof filter.activ !== "undefined") {
      url += `active=${filter.activ}`;
    }

    RestUtilities.get<EventModel[]>(url)
      .then((resp) => {
        this.setState({ data: resp.content, loading: false });
      })
      .catch((resp) => {
        this.showError(resp);
      });
  };

  copyEvent = (eventId: number): void => {
    this.setState({ loading: true });
    RestUtilities.get(`/api/Events/Copy?eventId=${eventId}`)
      .then((resp) => {
        this.handleNavigate(
          `${RoutePaths.EditEvent}?eventid=${resp.content}`
        );
      })
      .catch((resp) => {
        this.showError(resp);
      });
  };

  showError = (resp: IRestResponse<EventModel>): void => {
    if (typeof resp.error_content != "undefined" && resp.error_content) {
      this.setState({
        showError: true,
        errorMessage: resp.error_content,
        loading: false,
      });
    }
  };

  handleSnackbarClose = (event: any, reason: string) => {
    if (reason === "clickaway") return;

    this.setState({ showError: false });
  };

  componentDidMount() {
    this.fetchEventStatuses();
    this.fetchEvents();
  }

  public render() {
    let rows: JSX.Element[] = this.state.data
      .sort((a, b) => {
        return a.eventFrom > b.eventFrom ? -1 : 1;
      })
      .map((x) => {
        return this.mapRowCells(x);
      });

    let columns: JSX.Element[] = EventsComponent.TableColumns.map((x) => {
      return <TableCell key={x}>{x}</TableCell>;
    });

    let statusOptions: JSX.Element[] = this.state.statuses.map((x) => {
      return (
        <MenuItem key={x.id} value={x.id}>
          {x.description}
        </MenuItem>
      );
    });

    return (
      <>
        <h2 className="mt-3 title">Veranstaltungen</h2>

        <div className="card">
          <div className="card-header">
            <h5>Filters</h5>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-sm-5">
                <TextField
                  label="Bezeichnung:"
                  name="eventName"
                  value={this.state.filter.eventName}
                  onChange={(e: any) => this.handleFilterChange(e)}
                  fullWidth
                ></TextField>
              </div>
              <div className="col-sm-5 offset-sm-1">
                <DatePicker
                  label="Beginndatum von:"
                  value={this.state.filter.eventFrom ? new Date(this.state.filter.eventFrom) : null}
                  format={Constants.DateFormat}
                  onChange={(date: any) => {
                    let event = {
                      target: {
                        name: "eventFrom",
                        value: date,
                      },
                    };
                    this.handleFilterChange(event);
                  }}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-sm-5">
                <TextField
                  label="Beschreibung:"
                  name="eventDescription"
                  value={this.state.filter.eventDescription}
                  onChange={(e: any) => this.handleFilterChange(e)}
                  fullWidth
                ></TextField>
              </div>
              <div className="col-sm-5 offset-sm-1">
                <DatePicker
                  label="Beginndatum bis:"
                  value={this.state.filter.eventTo ? new Date(this.state.filter.eventTo) : null}
                  format={Constants.DateFormat}
                  onChange={(date: any) => {
                    let event = {
                      target: {
                        name: "eventTo",
                        value: date,
                      },
                    };
                    this.handleFilterChange(event);
                  }}
                />
              </div>
            </div>
            <div className="row mt-5">
              <div className="col-sm-5">
                <FormControl variant="outlined" fullWidth>
                  <InputLabel htmlFor="outlined-aktiv">Aktiv</InputLabel>
                  <Select
                    input={<OutlinedInput id="outlined-aktiv" fullWidth />}
                    name="activ"
                    value={
                      typeof this.state.filter.activ != "undefined"
                        ? this.state.filter.activ
                        : ""
                    }
                    onChange={(e: any) => this.handleFilterChange(e)}
                    fullWidth
                  >
                    <MenuItem value="">
                      <em></em>
                    </MenuItem>
                    <MenuItem value={"true"}>Ja</MenuItem>
                    <MenuItem value={"false"}>Nein</MenuItem>
                  </Select>
                </FormControl>
              </div>
              <div className="col-sm-5 offset-sm-1">
                <FormControl variant="outlined" fullWidth>
                  <InputLabel htmlFor="outlined-aktiv">Status</InputLabel>
                  <Select
                    input={<OutlinedInput id="outlined-aktiv" fullWidth />}
                    fullWidth
                    name="eventStatusId"
                    value={
                      typeof this.state.filter.eventStatusId != "undefined"
                        ? this.state.filter.eventStatusId
                        : ""
                    }
                    onChange={(e: any) => this.handleFilterChange(e)}
                  >
                    <MenuItem value="">
                      <em></em>
                    </MenuItem>
                    {statusOptions}
                  </Select>
                </FormControl>
              </div>
            </div>
            <div className="float-right mt-4">
              <Button
                variant="contained"
                color="primary"
                className="btnBlue"
                onClick={() => this.handleSearch()}
              >
                Suchen
              </Button>
              <Button
                className="ml-2 btnBlue"
                color="primary"
                variant="contained"
                onClick={() => this.clearFilters()}
              >
                Zurücksetzen
              </Button>
            </div>
          </div>
        </div>
        <div className="row">
          <Button
            className="mt-3 btnBlue"
            color="primary"
            variant="contained"
            onClick={(e: any) => this.handleNavigate(RoutePaths.EditEvent)}
          >
            Hinzufügen
          </Button>
        </div>
        <div className="row mt-3">
          <Table className="objects-table table">
            <TableHead>
              <TableRow>{columns}</TableRow>
            </TableHead>
            <TableBody>{rows}</TableBody>
          </Table>
        </div>
        <GrowMessage
                anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
                }}
                message={this.state.errorMessage}
                visible={this.state.showError}
                autoHideDuration={6000}
                onClose={this.handleSnackbarClose} ></GrowMessage>  
        {/* <Snackbar
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          open={this.state.showError}
          autoHideDuration={6000}
          onClose={this.handleSnackbarClose}
        >
          <SnackbarWrapper
            onClose={this.handleSnackbarClose}
            variant="success"
            message={this.state.errorMessage}
          />
        </Snackbar> */}
        {this.state.loading && <Loader />}
      </>
    );
  }
}

export default withRouter(EventsComponent);
